import { GanttChartData } from "../types/ganttChart";
import { DragTelemetryRequest } from "../types/dragTelemetry";
import { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import api, { API_PATHS } from "./api";

// axios 1.6 sends through XHR, which the browser cancels when the page unloads, so the report
// sent on reload or tab close was often lost. A keepalive fetch survives the unload, and running
// it as an adapter keeps the auth and responsible-user headers added by the interceptors.
const keepaliveAdapter: AxiosAdapter = async (
  config: InternalAxiosRequestConfig,
): Promise<AxiosResponse> => {
  const response = await fetch(api.getUri(config), {
    method: "POST",
    keepalive: true,
    headers: config.headers.toJSON() as Record<string, string>,
    body: config.data as string,
  });
  return {
    data: null,
    status: response.status,
    statusText: response.statusText,
    headers: {},
    config,
  };
};

// New TaskLocation types
export type ShiftLocation = {
  type: "shift";
  shiftId: number;
};

export type UnplannedLocation = {
  type: "unplanned";
};

export type TaskLocation = ShiftLocation | UnplannedLocation;

// Enhanced command interface
export interface MoveWorkTaskDragAndDropCommand {
  workTaskId: number;
  destinationWorkTaskIdBefore: number | null;
  destinationWorkTaskIdAfter: number | null;
  from: TaskLocation;
  to: TaskLocation;

  // Legacy fields - kept for backward compatibility during transition
  /** @deprecated Use 'from' field instead */
  fromShiftId?: number;
  /** @deprecated Use 'to' field instead */
  toShiftId?: number;
}

// Helper functions for creating TaskLocations
export const createShiftLocation = (shiftId: number): ShiftLocation => ({
  type: "shift",
  shiftId,
});

export const createUnplannedLocation = (): UnplannedLocation => ({
  type: "unplanned",
});

// Type guards
export const isShiftLocation = (
  location: TaskLocation,
): location is ShiftLocation => location.type === "shift";

export const isUnplannedLocation = (
  location: TaskLocation,
): location is UnplannedLocation => location.type === "unplanned";

export interface EditRequest {
  command: MoveWorkTaskDragAndDropCommand;
  initialState?: GanttChartData; // Only needed when starting a new session
}

export interface InitiateRequest {
  initialState: GanttChartData;
}

export interface CommandResponse {
  state: GanttChartData | null;
  success: boolean;
  message: string;
}

export interface SessionStatusResponse {
  canUndo: boolean;
  canRedo: boolean;
  hasActiveSession: boolean;
  message: string;
  rulesBroken?: number;
  totalRules?: number;
}

export interface LockedStatusResponse {
  locked: boolean;
  lockedBy: string | null;
}

export interface SaveResponse {
  success: boolean;
  message: string;
}

export interface EndSessionResponse {
  success: boolean;
  message: string;
}
export interface InitiateSessionResponse {
  success: boolean;
  message: string;
  state: GanttChartData;
}

export class DragAndDropApi {
  private static readonly BASE_PATH = "/drag-and-drop";

  /**
   * Execute a drag-and-drop command. If no session exists, it will be created automatically
   * using the provided initialState.
   */
  static async executeCommand(
    command: MoveWorkTaskDragAndDropCommand,
    initialState?: GanttChartData,
  ): Promise<CommandResponse> {
    const request: EditRequest = {
      command,
      initialState,
    };

    const response = await api.post(`${this.BASE_PATH}/session/edit`, request);
    return response.data;
  }

  /**
   * Undo the last command in the current session
   */
  static async undo(): Promise<CommandResponse> {
    const response = await api.post(`${this.BASE_PATH}/undo`);
    return response.data;
  }

  /**
   * Redo the last undone command in the current session
   */
  static async redo(): Promise<CommandResponse> {
    const response = await api.post(`${this.BASE_PATH}/redo`);
    return response.data;
  }

  /**
   * Get the current session status including state and undo/redo availability
   */
  static async getSessionStatus(): Promise<SessionStatusResponse> {
    const response = await api.get(`${this.BASE_PATH}/session/status`);
    return response.data;
  }

  /**
   * Check if a specific planning date and filter combination is locked
   */
  static async getLockStatus(
    planningDate: string,
    idActiveNexusFilter: number,
  ): Promise<LockedStatusResponse> {
    const response = await api.get(`${this.BASE_PATH}/session/lock-status`, {
      params: {
        planningDate,
        idActiveNexusFilter,
      },
    });
    return response.data;
  }

  /**
   * Save the current session state to database and finalize the plan
   */
  static async saveAndFinalize(): Promise<SaveResponse> {
    const response = await api.post(
      `${this.BASE_PATH}/session/save-and-finalize`,
    );
    return response.data;
  }

  /**
   * Inititae new session based on current optimised plan.
   */

  static async initiateSession(
    initialState: GanttChartData,
  ): Promise<InitiateSessionResponse> {
    const request: InitiateRequest = {
      initialState,
    };
    const response = await api.post(`${this.BASE_PATH}/handshake`, request);
    return response.data;
  }

  /**
   * End the current session without saving (discard changes)
   */
  static async endSession(): Promise<EndSessionResponse> {
    const response = await api.post(`${this.BASE_PATH}/session/end`);
    return response.data;
  }

  /**
   * Send the summary of one drag to the backend log
   */
  static async sendDragTelemetry(request: DragTelemetryRequest): Promise<void> {
    await api.post(API_PATHS.dragTelemetry, request, {
      adapter: keepaliveAdapter,
    });
  }
}
